/**
 * Underlined view tabs, drawn as finixt-ui's Tabs with the roles the package
 * leaves out: the row is a tablist, each tab says whether it is selected,
 * and the arrow keys move between them.
 */
import { useState, type CSSProperties, type KeyboardEvent } from 'react';

import { Icon } from './finixt.js';

export interface TabItem<T extends string = string> {
  value: T;
  label: string;
  icon?: string | undefined;
  /** Quiet mono count after the label. */
  count?: number | string | undefined;
}

export interface TabsProps<T extends string = string> {
  items: TabItem<T>[];
  value: T;
  onChange: (value: T) => void;
  style?: CSSProperties | undefined;
}

export function Tabs<T extends string = string>({ items, value, onChange, style }: TabsProps<T>) {
  const [hover, setHover] = useState<T | null>(null);
  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const index = items.findIndex((item) => item.value === value);
    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (!step || index < 0) return;
    event.preventDefault();
    const next = items[(index + step + items.length) % items.length];
    if (next) onChange(next.value);
  }
  return (
    <div
      role="tablist"
      onKeyDown={onKeyDown}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        borderBottom: '1px solid var(--border-hairline)',
        ...style,
      }}
    >
      {items.map((item) => {
        const on = value === item.value;
        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={on}
            tabIndex={on ? 0 : -1}
            onClick={() => onChange(item.value)}
            onMouseEnter={() => setHover(item.value)}
            onMouseLeave={() => setHover(null)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              padding: '9px 11px',
              marginBottom: -1,
              background: 'transparent',
              border: 'none',
              borderBottom: `2px solid ${on ? 'var(--surface-inverse)' : 'transparent'}`,
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-body-sm)',
              fontWeight: on ? 'var(--weight-semibold)' : 'var(--weight-medium)',
              color: on
                ? 'var(--text-strong)'
                : hover === item.value
                  ? 'var(--text-body)'
                  : 'var(--text-soft)',
              cursor: 'pointer',
              transition: 'color var(--dur-micro) var(--ease-standard)',
              textAlign: 'left',
            }}
          >
            {item.icon ? <Icon name={item.icon} size={16} /> : null}
            {item.label}
            {item.count !== undefined ? (
              <span
                className="mono"
                style={{ fontSize: 'var(--text-label-sm)', color: 'var(--text-faint)' }}
              >
                {item.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
